import React from 'react';
import { Flex, theme } from 'antd';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import dayjs from 'dayjs';

import parseStatusCodesRawData from './parseStatusCodesRawData';
import sumLists from '../sumLists';
import StatusCodesMainChart, { MAIN_CHART_ID } from './StatusCodesMainChart';
import { LegendLabelSquare, ChartCard } from '~components/Charts';
import { ColorsList } from '../Chart';
import statusCodesDictionary from './statusCodesDictionary';
import { describeDateRange } from '~components/Filters';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackDashboardV3Event from '../useTrackDashboardV3Event';
import { type WidgetLoadError } from '~features/empty-states';
import ChartLoadError from '../components/ChartLoadError';

export const STATUS_CODES_VISIBILITY_CHARTS: ChartConfig[] = [{ id: MAIN_CHART_ID, type: 'dataset' }];

const MAX_STATUS_CODES_DISPLAYABLE = 7;
export const TOOLTIP_MESSAGE =
  'The status codes that your pages return to crawlers over your selected period. You should mostly be returning OK (200) status codes. Monitoring this helps identify potential errors.';

export type ChartStatusCodesProps = {
  rawData?: AggregatedMetrics.StatusCodesResult;
  isLoading: boolean;
  error?: WidgetLoadError;
  dateRange: [Date, Date];
  descriptionVisible?: boolean;
};

function ChartStatusCodesCard({
  rawData,
  isLoading,
  error,
  dateRange,
  descriptionVisible = false,
}: ChartStatusCodesProps) {
  const activeFilterLabel = React.useMemo(() => describeDateRange(dayjs(dateRange[0]), dayjs(dateRange[1])), [dateRange]);

  return (
    <ChartCard
      title={`Status Codes (${activeFilterLabel})`}
      description={TOOLTIP_MESSAGE}
      descriptionType={descriptionVisible ? 'paragraph' : 'tooltip'}
      isLoading={isLoading}
      errorFallback={error && <ChartLoadError onRetry={error.onRetry} />}
    >
      {rawData && <ChartPagesCrawled rawData={rawData} />}
    </ChartCard>
  );
}

function ChartPagesCrawled({ rawData }: { rawData: AggregatedMetrics.StatusCodesResult }) {
  const track = useTrackDashboardV3Event();
  const ChartItemsVisibility = useChartItemsVisibility(STATUS_CODES_VISIBILITY_CHARTS);
  const { token } = theme.useToken();

  const emptyDataPoint = Array.from({ length: rawData.intervals.length }, () => 0);

  const data = React.useMemo(() => parseStatusCodesRawData(rawData), [rawData]);

  const statusCodesData = React.useMemo(() => {
    const displayableCrawlers = data.statusCodesValuesList.slice(0, MAX_STATUS_CODES_DISPLAYABLE - 1);

    if (data.statusCodesValuesList.length > MAX_STATUS_CODES_DISPLAYABLE - 1) {
      const otherCrawlersData = sumLists(
        data.statusCodesValuesList.slice(MAX_STATUS_CODES_DISPLAYABLE - 1).map(([_, data]) => data)
      );
      displayableCrawlers.push(['other', otherCrawlersData]);
    }

    return displayableCrawlers;
  }, [data.statusCodesValuesList]);

  const visibleStatusCodeData: [string, number[]][] = statusCodesData.map((originalData, index) =>
    ChartItemsVisibility.itemsVisibility[index] ? originalData : [originalData[0], emptyDataPoint]
  );

  const totalResponsesPerPeriod = React.useMemo(() => {
    return rawData.intervals.map((_, i) => {
      return visibleStatusCodeData.reduceRight((acc, dataPerStatusCode) => dataPerStatusCode[1][i] + acc, 0);
    }, []);
  }, [visibleStatusCodeData, rawData.intervals]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'status codes',
      dataset: statusCodesData[datasetIndex][0],
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(statusCodesData.length);
  }, [statusCodesData]);

  return (
    <ChartCard.Content>
      <Flex style={{ marginBottom: token.marginXL }} gap={token.sizeSM} align="flex-end" wrap="wrap">
        {statusCodesData.map(([statusCode], i) => (
          <div key={statusCode}>
            <LegendLabelSquare
              color={ColorsList[i]}
              label={statusCode === 'other' ? 'Other' : `${statusCodesDictionary[statusCode] ?? ''} (${statusCode})`}
              crossedOut={!ChartItemsVisibility.itemsVisibility[i]}
              onClick={() => onLegendItemClicked(i)}
            />
          </div>
        ))}
      </Flex>
      <ChartCard.ChartLayout>
        <StatusCodesMainChart
          totalResponsesPerPeriod={totalResponsesPerPeriod}
          statusCodesData={visibleStatusCodeData}
          intervals={rawData.intervals}
          granularity={rawData.granularity}
        />
      </ChartCard.ChartLayout>
    </ChartCard.Content>
  );
}

export default ChartStatusCodesCard;
